import { getTimeDelta } from "./dateTime";
import Cookies from 'js-cookie';

const debugLog = (msg:any) => undefined; //console.log(msg);

interface IMyChartWidgetConfiguration {
    url: string;
    apiKey: string;
    widgetType: string;
    containerId: string;
    additionalParams: {
        department: string | number;
        visittype: string | number;
        provider: string | number;
        numdays: string | number;
        numslotsperday: string | number;
    };
}

interface IMyChartWidgetProviderConfiguration {
    url: string;
    apiKey: string;
    departmentID: string | number;
    visitTypeID: string | number;
    providerID: string | number;
    numberOfDays: string | number;
    numberOfSlotsPerDay: string | number;
    openSchedulingSearchResultsEnabled: boolean;
}

interface IMyChartWidget {
    renderWidget: (configuration: IMyChartWidgetConfiguration) => void;
}

declare global {
    interface Window {
        mychartWidget: IMyChartWidget;
        myChartConfig: IMyChartWidgetConfiguration;
        myChartProviderConfig: IMyChartWidgetProviderConfiguration;
    }
}

interface IMyChartGlobals {
    myChartWidget: IMyChartWidget;
    myChartProviderConfig: IMyChartWidgetProviderConfiguration;
}

const getMyChartGlobals = (): IMyChartGlobals => {
    /**
     * Simple getter to access the globals set by the parent page.
     */
    return {
        myChartWidget: window.mychartWidget,
        myChartProviderConfig: window.myChartProviderConfig
    };
};

export const getMyChartCurrentProvider = ():IMyChartWidgetProviderConfiguration => JSON.parse(JSON.stringify(window.myChartProviderConfig));

export interface IMyChartOpenSchedulingTime {
    dateStr: string;
    timeStr: string;
    dateTime: Date;
    humanDelta: string;
}

interface IMyChartSchedulingCachedResult {
    expiration: number;
    result: IMyChartOpenSchedulingTime;
}

const getCachedResultKey = (prov: string | number, dept: string | number, visit: string | number) => {
    return `getFirstOpenSchedulingSlot-${prov},${dept},${visit}`;
};

const loadCachedResult = (prov: string | number, dept: string | number, visit: string | number) => {
    try {

        const cachedResult = Cookies.get(getCachedResultKey(prov, dept, visit));
        if (cachedResult) {
            const lastResult = JSON.parse(cachedResult);
            if(lastResult.expiration > new Date().valueOf()) {
                lastResult.result.dateTime = new Date(Date.parse(lastResult.result.dateTime));
                debugLog(`getFirstOpenSchedulingSlot from cache: ${JSON.stringify(lastResult)}`);
                return lastResult.result;
            }
        }
        return false;
    } catch (err) {
        // If we experienced an error then it's possible the cookie was corrupted.
        // This could be caused by user fiddling so rather than excessive requests
        // as a result we just don't return any open scheduling times.
        return null;
    }
};

const storeCachedResult = (prov: string | number, dept: string | number, visit: string | number, result?: IMyChartOpenSchedulingTime) => {
    const exp = new Date().setMinutes(new Date().getMinutes() + 5).valueOf();
    if(result === null || result === undefined) {
        Cookies.remove(getCachedResultKey(prov, dept, visit));
    } else {
        Cookies.set(getCachedResultKey(prov, dept, visit), JSON.stringify({
            expiration: exp,
            result: result
        }));
    }
};

const getRandomBase36Number = () => {
    const a = new Uint32Array(1);
    crypto.getRandomValues(a);
    return a[0].toString(36);
}

const getUniqueID = () => `SMCRIH_${getRandomBase36Number()}_${new Date().valueOf().toString(36)}`

export const resetResultCache = (prov: string | number, dept: string | number, visit: string | number) => storeCachedResult(prov, dept, visit, undefined);

type MyChartOpenSchedulingTimePromise = Promise<IMyChartOpenSchedulingTime | null>;

const RETRY_LIMIT = 1;
const MYCHART_WIDGET_TIMEOUT = 10000;

/**
 *
 * @param prov MyChart Provider ID
 * @param dept MyChart Department ID
 * @param visit MyChart Visit Type ID
 * @param retryNumber _internal_ Used to track how many retries are made for this particular prov, dept, visit request.
 * @returns Promise fulfilling with an IMyChartOpenSchedulingTime object containing the first appointment slot date and time if found.
 */
const getFirstOpenSchedulingSlot = (prov: string | number, dept: string | number, visit: string | number, retryNumber?:number): MyChartOpenSchedulingTimePromise => {
    debugLog(`getFirstOpenSchedulingSlot(${prov}, ${dept}, ${visit}, ${retryNumber})`);
    const { myChartWidget, myChartProviderConfig } = getMyChartGlobals();

    var cachedResult:any = undefined;

    try {
        cachedResult = loadCachedResult(prov, dept, visit);
        // False means the cached result expired so we set it to undefined
        // and don't pass back any cached results.
        if(cachedResult === false) {
            cachedResult = undefined;
        }
    } catch (err) {
        // If we experienced an error then it's possible the cookie was corrupted.
        // This could be caused by user fiddling so rather than excessive requests
        // as a result we just don't return any open scheduling times.
        cachedResult = null;
    }

    if(cachedResult !== undefined) {
        return new Promise((done: (result: IMyChartOpenSchedulingTime | null) => void, error: (error: string) => void) => {
            done(cachedResult);
        });
    }

    var el = document.createElement('div');
    el.style.display = 'none'
    el.id = getUniqueID();
    document.body.appendChild(el);

    const prom = new Promise((done: (result: IMyChartOpenSchedulingTime | null) => void, error: (error: string) => void) => {
        const TIMEOUT_LIMIT = MYCHART_WIDGET_TIMEOUT;
        var timeoutID: number | undefined;
        var found = false;

        const cleanup = () => {
            debugLog(`getFirstOpenSchedulingSlot cleanup called`);
            // debugLog(`getFirstOpenSchedulingSlot cleanup() el.innerHTML => ${el.innerHTML}`);
            if (timeoutID !== null) {
                clearTimeout(timeoutID);
                timeoutID = undefined;
            }
            observer.disconnect();
            el.remove();
            debugLog('Cleaned up observer, open scheduling container element, and timeout.');
            if (!found) {
                if(retryNumber !== undefined && retryNumber >= RETRY_LIMIT) {
                    error("TIMEOUT");
                } else {
                    debugLog(`getFirstOpenSchedulingSlot - Making a retry call (retry=${retryNumber})`);
                    getFirstOpenSchedulingSlot(prov, dept, visit, (retryNumber || 0)+1)
                        .then(done, error);
                }
            }
        };

        /**
         * MutationObserver is used to detect when MyChart widget has finished loading.
         */
        const observer = new MutationObserver((/*ml, ob*/) => {
            const day = el.querySelector<HTMLSpanElement>('.myc_dayHeader');
            const time = el.querySelector<HTMLSpanElement>('.myc_timeSlot');
            const noData = el.querySelector<HTMLSpanElement>('.myc_noData');

            debugLog(`MyChart OpenScheduling MutationObserver: ${day}, ${time}, ${noData}`);

            if (!!noData) {
                found = true;
                debugLog(`MyChart OpenScheduling no times available`);
                cleanup();
                done(null);
            } else if (!!day && !!time) {
                found = true;
                const parsedDateTime = new Date(Date.parse(day.innerText + ' ' + time.innerText));

                var result = {
                    dateStr: day.innerText,
                    timeStr: time.innerText,
                    dateTime: parsedDateTime,
                    humanDelta: getTimeDelta(parsedDateTime),
                };
                debugLog(`MyChart OpenScheduling Result: ${result}`);
                cleanup();
                storeCachedResult(prov, dept, visit, result);
                done(result);
            }
        });

        observer.observe(el, {
            attributes: false,
            childList: true,
            subtree: true
        });

        timeoutID = window.setTimeout(cleanup, TIMEOUT_LIMIT);

    });

    // Render the MyChart widget to our hidden element requesting 1 day and 1 time slot.
    myChartWidget.renderWidget({
        "url": myChartProviderConfig.url,
        "apiKey": myChartProviderConfig.apiKey,
        "widgetType": "openschedulingsimple",
        "containerId": el.id,
        "additionalParams": {
            "department": dept,
            "visittype": visit,
            "provider": prov,
            "numdays": 1,
            "numslotsperday": 1
        }
    });

    return prom;
};

const getCurrentProvidersFirstOpenSchedulingSlot = () => {
    const { myChartProviderConfig } = getMyChartGlobals();

    return getFirstOpenSchedulingSlot(
        myChartProviderConfig.providerID,
        myChartProviderConfig.departmentID,
        myChartProviderConfig.visitTypeID
    );
};

var mychartRendered: boolean = false;
const renderMyChartOnProviderProfile = () => {
    const { myChartWidget, myChartProviderConfig } = getMyChartGlobals();

    if (mychartRendered) {
        return;
    }
    mychartRendered = true;
    myChartWidget.renderWidget({
        "url": myChartProviderConfig.url,
        "apiKey": myChartProviderConfig.apiKey,
        "widgetType": "openschedulingsimple",
        "containerId": "mychartTimes",
        "additionalParams": {
            "department": myChartProviderConfig.departmentID,
            "visittype": myChartProviderConfig.visitTypeID,
            "provider": myChartProviderConfig.providerID,
            "numdays": myChartProviderConfig.numberOfDays,
            "numslotsperday": myChartProviderConfig.numberOfSlotsPerDay
        }
    });
};

export {
    renderMyChartOnProviderProfile,
    getFirstOpenSchedulingSlot,
    getCurrentProvidersFirstOpenSchedulingSlot,
};
